import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { loadCatalogo } from "../../actions/catalogo";
import HeaderCatalogo from "./HeaderCatalogo";
import GridCatalogo from "./GridCatalogo";
import { Helmet } from "react-helmet-async";
import { getSessionId } from "../../utils/index";
import Footer from "./Footer";
import CatalogoSkeleton from "./CatalogoSkeleton";
import CatalogoFloatingControls from "./CatalogoFloatingControls";
import {
  createMetaEventId,
  getMetaBrowserIdentifiers,
  initializeMetaPixel,
  sendMetaPageView,
} from "../../utils/metaPixel";
import {
  PRIVACY_CONSENT_CHANGE_EVENT,
  isAnalyticsConsentEnabled,
  isMarketingConsentEnabled,
} from "../../utils/privacyConsent";
import { getAbsoluteStoreProfilePictureUrl } from "../../utils/storeProfilePicture";

function Catalogo() {
  const { username } = useParams();
  const dispatch = useDispatch();
  const { loja, error } = useSelector((state) => state.catalogo);

  const scrolls = useRef(0);
  const fotosVisualizadas = useRef(0);
  const tempoInicio = useRef(Date.now());
  const lastScrollY = useRef(window.scrollY);
  const injectedHeadNodesRef = useRef([]);
  const pageViewEventIdRef = useRef(null);
  const [privacyConsentTick, setPrivacyConsentTick] = React.useState(0);

  useEffect(() => {
    if (username) {
      dispatch(loadCatalogo(username));
    }
  }, [dispatch, username]);

  useEffect(() => {
    if (!loja?.pixel_meta_id || !isMarketingConsentEnabled()) return;
    const initialized = initializeMetaPixel(loja.pixel_meta_id);
    if (!initialized) return;

    if (!pageViewEventIdRef.current) {
      pageViewEventIdRef.current = createMetaEventId("pv");
    }

    sendMetaPageView({ eventId: pageViewEventIdRef.current });
  }, [loja?.pixel_meta_id, privacyConsentTick]);

  useEffect(() => {
    const onPrivacyConsentChange = () => {
      setPrivacyConsentTick((value) => value + 1);
    };

    window.addEventListener(
      PRIVACY_CONSENT_CHANGE_EVENT,
      onPrivacyConsentChange
    );

    return () => {
      window.removeEventListener(
        PRIVACY_CONSENT_CHANGE_EVENT,
        onPrivacyConsentChange
      );
    };
  }, []);

  // useEffect(() => {
  //   fotosVisualizadas.current = 0; // reseta sempre que muda a loja
  // }, [produtos]);

  useEffect(() => {
    if (!loja) return;

    const handleScroll = () => {
      const atualScroll = window.scrollY;
      if (atualScroll - lastScrollY.current >= 300) {
        scrolls.current += 1;
        lastScrollY.current = atualScroll;
      }
    };

    let beaconEnviado = false;

    const handleUnload = () => {
      if (!isAnalyticsConsentEnabled()) return;
      if (beaconEnviado) return;
      beaconEnviado = true;

      const tempoFinal = Date.now() - tempoInicio.current;
      const sessionId = getSessionId();

      const payload = {
        loja_id: loja.id,
        session_id: sessionId,
        scrolls: scrolls.current,
        fotos_carregadas: fotosVisualizadas.current,
        duracao_ms: tempoFinal,
        device: /Mobi|Android/i.test(navigator.userAgent)
          ? "mobile"
          : "desktop",
        referrer: document.referrer || "direto",
        meta_event_id: pageViewEventIdRef.current || createMetaEventId("pv"),
        event_source_url: window.location.href,
      };

      const metaIds = getMetaBrowserIdentifiers();
      if (metaIds.fbp) payload.fbp = metaIds.fbp;
      if (metaIds.fbc) payload.fbc = metaIds.fbc;

      const blob = new Blob([JSON.stringify(payload)], {
        type: "application/json",
      });

      navigator.sendBeacon(
        `${process.env.REACT_APP_API_URL}/public/analise/visita`,
        blob
      );
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("pagehide", handleUnload);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("beforeunload", handleUnload);
      window.removeEventListener("pagehide", handleUnload);
    };
  }, [loja, privacyConsentTick]);

  useEffect(() => {
    const cleanupInjectedNodes = () => {
      injectedHeadNodesRef.current.forEach((node) => {
        if (node && node.parentNode) {
          node.parentNode.removeChild(node);
        }
      });
      injectedHeadNodesRef.current = [];
    };

    cleanupInjectedNodes();

    const rawHeadCode = isMarketingConsentEnabled() ? loja?.custom_head_code : null;
    if (!rawHeadCode || !rawHeadCode.trim()) {
      return cleanupInjectedNodes;
    }

    try {
      const parser = new DOMParser();
      const parsed = parser.parseFromString(rawHeadCode, "text/html");
      const nodes = [...parsed.head.childNodes, ...parsed.body.childNodes];

      nodes.forEach((node) => {
        if (node.nodeType !== 1 && node.nodeType !== 3) return;
        if (node.nodeType === 3 && !node.textContent.trim()) return;

        const tagName = node.tagName ? node.tagName.toLowerCase() : null;

        // Scripts precisam ser recriados para execução correta quando inseridos dinamicamente.
        if (tagName === "script") {
          const script = document.createElement("script");
          [...node.attributes].forEach((attr) => {
            script.setAttribute(attr.name, attr.value);
          });
          if (node.textContent) {
            script.textContent = node.textContent;
          }
          document.head.appendChild(script);
          injectedHeadNodesRef.current.push(script);
          return;
        }

        const clone = node.cloneNode(true);
        document.head.appendChild(clone);
        injectedHeadNodesRef.current.push(clone);
      });
    } catch (error) {
      console.error("Erro ao injetar custom_head_code no head:", error);
    }

    return cleanupInjectedNodes;
  }, [loja?.custom_head_code, privacyConsentTick]);

  if (error) return <p>Erro: {error}</p>;
  if (!loja) return <CatalogoSkeleton />;

  return (
    <>
      <Helmet>
        <title>
          {loja.seo_title ||
            `Confira nossa vitrine virtual - ${
              loja.nome_fantasia || "nossa loja"
            }`}
        </title>
        <meta
          name="description"
          content={
            loja.seo_description ||
            `Explore as peças disponíveis da ${
              loja.nome_fantasia || "nossa loja"
            } com praticidade. Veja tudo na vitrine virtual e chame no WhatsApp!`
          }
        />
        <meta
          name="keywords"
          content={
            loja.seo_keywords ||
            "vitrine virtual, loja de roupas, moda feminina, moda masculina, roupas estilosas, novidades da loja"
          }
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content={
            loja.seo_title ||
            `Confira nossa vitrine virtual - ${
              loja.nome_fantasia || "nossa loja"
            }`
          }
        />
        <meta
          property="og:description"
          content={
            loja.seo_description ||
            `Confira as novidades da ${
              loja.nome_fantasia || "nossa loja"
            } na vitrine virtual. Clique e fale com a gente no WhatsApp!`
          }
        />
        <meta
          property="og:image"
          content={getAbsoluteStoreProfilePictureUrl(
            loja.profile_picture,
            loja.updatedAt || loja.updated_at
          )}
        />
        <meta
          property="og:url"
          content={`https://vintree.com.br/${
            loja.usuario?.username || username
          }`}
        />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={
            loja.seo_title ||
            `Confira nossa vitrine virtual - ${
              loja.nome_fantasia || "nossa loja"
            }`
          }
        />
        <meta
          name="twitter:description"
          content={
            loja.seo_description ||
            `Dê uma olhada na vitrine virtual da ${
              loja.nome_fantasia || "nossa loja"
            } e descubra suas próximas peças favoritas.`
          }
        />
        <meta
          name="twitter:image"
          content={getAbsoluteStoreProfilePictureUrl(
            loja.profile_picture,
            loja.updatedAt || loja.updated_at
          )}
        />
      </Helmet>

      <div className="mx-auto">
        <CatalogoFloatingControls loja={loja} username={username} />
        <HeaderCatalogo loja={loja} />
        <GridCatalogo
          username={username}
          loja={loja}
          tags={loja?.tags}
          contarFotoVisualizada={() => {
            fotosVisualizadas.current += 1;
          }}
        />
        <Footer loja={loja} />
      </div>
    </>
  );
}

export default Catalogo;
